<template>
  <div class="backdrop">
    <div
      :style="popupMaxHeight"
      class="dialog"
      :class="{'overflow-initial':overflowInitial,'overflow-hidden':overflowHidden}"
      role="dialog"
    >
      <button
        v-if="closeButton"
        :class="[
          'dialog__close-button',
          `variant-${closeButtonVariant}`,
        ]"
        type="button"
        aria-label="Close dialog"
        @click="close"
      >
        <CrossIcon
          width="18"
          height="18"
        />
      </button>

      <form
        v-if="isDefaultMarkupShown"
        class="popup-form"
        @submit.prevent="handleSubmitButtonClick"
      >
        <div class="popup-form__capture">
          {{ title }}
        </div>
        <div
          class="popup-form__body"
          :class="{'fixed':buttonsFixed}"
        >
          <slot name="body"/>
        </div>

        <div
          class="popup-form__button-group"
          :class="{'fixed': buttonsFixed}"
        >
          <TButton
            v-if="$listeners.submit"
            :loading="loading"
            :disabled="submitButtonDisabled"
            @click="handleSubmitButtonClick"
          >
            {{ submitButtonName }}
          </TButton>
          <TButton
            v-if="$listeners.cancel"
            :loading="loading"
            variant="secondary"
            @click="handleCancelButtonClick"
          >
            {{ cancelButtonName }}
          </TButton>
        </div>

      </form>
      <slot></slot>
    </div>
  </div>
</template>

<script>
import popup from '@/mixins/popup';
import { TButton } from '@/components/base/TButton';
import CrossIcon from '@/assets/icons/cross.svg?inline';


const CLOSE_BUTTON_VARIANTS = {
  GREY: 'grey',
  BLACK: 'black',
};

export default {
  name: 'PopUp',
  mixins: [popup],
  inject: [
    'screen',
  ],
  components: {
    TButton,
    CrossIcon,
  },
  props: {
    closeButton: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: [String, Number],
    },
    overflowInitial: {
      type: Boolean,
      default: false,
    },
    overflowHidden: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: '',
    },
    submitButtonName: {
      type: String,
      default: 'Save',
    },
    cancelButtonName: {
      type: String,
      default: 'Cancel',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    submitButtonDisabled: {
      type: Boolean,
      default: false,
    },
    closeButtonVariant: {
      type: String,
      default: CLOSE_BUTTON_VARIANTS.GREY,
      validator(value) {
        return Object.values(CLOSE_BUTTON_VARIANTS).includes(value);
      },
    },
    buttonsFixed: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    popupMaxHeight() {
      if (typeof this.maxHeight === 'string') {
        return `max-height: ${this.maxHeight};`;
      }
      return this.maxHeight ? `max-height:${this.maxHeight}vh;` : 'max-height:80vh;';
    },
    isDefaultMarkupShown() {
      return this.$slots.body;
    },
  },
  methods: {
    handleSubmitButtonClick() {
      this.$emit('submit');
    },
    handleCancelButtonClick() {
      this.$emit('cancel');
    },
  },
};
</script>

<style lang="scss">
@import "~@/assets/scss/mixins";

.dialog {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;

  max-height: 80vh;
  background-color: var(--light);
  border-radius: var(--border-radius) var(--border-radius) 0 0;
  overflow-y: auto;

  @include media-lg() {
    top: 50%;
    right: auto;
    bottom: auto;
    left: 50%;
    transform: translate(-50%, -50%);

    border-radius: var(--border-radius);
  }

  &.overflow-initial {
    overflow-y: initial;
  }

  &.overflow-hidden {
    overflow-y: hidden;
    height: 70vh;
  }

  &__close-button {
    position: absolute;
    top: 24px;
    right: 24px;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 32px;
    height: 32px;
    border-radius: 50%;

    background-color: transparent;
    z-index: 1;

    &:focus-visible {
      outline: 1px solid var(--gray-1);
    }

    img {
      display: block;
    }

    &.variant {
      &-grey {
        background-color: var(--muted);

        svg {
          stroke: var(--gray-1);
        }
      }

      &-black {
        background-color: var(--gray-1);

        svg {
          stroke: var(--light);
        }
      }
    }
  }

  .popup-form__body {
    &.fixed {
      margin-bottom: 105px;
    }
  }
}
</style>
